/**
 * PartnerOrders — Team-Ansicht (Admin/Backoffice) für eingehende
 * Provisionspartner-Aufträge: übernehmen (→ Akte), Status pflegen,
 * Provision anpassen, Auszahlung markieren.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Camera, CheckCircle2, Clock, FileText, ArrowRight, Banknote,
  Euro, Phone, Mail, RefreshCcw,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const STATUS_META = {
  submitted: { label: "Eingereicht", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  in_progress: { label: "In Bearbeitung", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  report_done: { label: "Gutachten fertig", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  paid: { label: "Ausgezahlt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function OrderCard({ order, token, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(order.commission_amount);
  const m = STATUS_META[order.status] || STATUS_META.submitted;
  const cust = [order.customer_firstname, order.customer_name].filter(Boolean).join(" ");

  const takeOver = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-orders/${order.id}/take-over`);
      toast.success(`Akte ${data.case_number} angelegt`);
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const markPaid = async () => {
    if (!window.confirm(`${fmtEUR(order.commission_amount)} Provision an ${order.partner?.name || "Partner"} als ausgezahlt markieren?`)) return;
    setBusy(true);
    try {
      await api.post(`/partner-orders/${order.id}/mark-paid`);
      toast.success("Provision als ausgezahlt markiert");
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const saveAmount = async () => {
    setBusy(true);
    try {
      await api.patch(`/partner-orders/${order.id}`, { commission_amount: parseFloat(amount) || 0 });
      toast.success("Provision angepasst");
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`team-order-${order.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-base">{cust || "Unbekannter Kunde"}</span>
            {order.license_plate && <Badge variant="outline" className="font-mono text-xs">{order.license_plate}</Badge>}
            <Badge variant="outline" className={`${m.cls} text-[11px] font-bold`}>{m.label}</Badge>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Partner: <strong className="text-zinc-700">{order.partner?.name || "—"}</strong>
            {order.partner?.email ? ` (${order.partner.email})` : ""} · Eingereicht {fmtDate(order.created_at)}
            {order.accident_date ? ` · Unfall: ${fmtDate(order.accident_date)}` : ""}
            {order.insurance_name ? ` · Versicherung: ${order.insurance_name}` : ""}
            {order.opponent_plate ? ` · Gegner-Kz.: ${order.opponent_plate}` : ""}
            {(order.vehicle_make || order.vehicle_model) ? ` · ${[order.vehicle_make, order.vehicle_model].filter(Boolean).join(" ")}` : ""}
          </div>
          {(order.phone || order.email) && (
            <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3">
              {order.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{order.phone}</span>}
              {order.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{order.email}</span>}
            </div>
          )}
          {order.note && <div className="text-xs text-zinc-600 mt-1.5 bg-zinc-50 rounded-lg px-2.5 py-1.5 max-w-xl">{order.accident_type ? `[${order.accident_type}] ` : ""}{order.note}</div>}
          {!order.note && order.accident_type && <div className="text-xs text-zinc-600 mt-1.5 bg-zinc-50 rounded-lg px-2.5 py-1.5 max-w-xl">Unfallart: {order.accident_type}</div>}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Provision</div>
          <div className="flex items-center gap-1.5 mt-1">
            <Input
              type="number" min="0" step="10"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={order.status === "paid"}
              className="w-24 h-8 text-right font-bold tabular-nums"
              data-testid={`team-order-amount-${order.id}`}
            />
            <span className="text-sm font-bold">€</span>
            {Number(amount) !== Number(order.commission_amount) && (
              <Button size="sm" variant="outline" className="h-8" onClick={saveAmount} disabled={busy} data-testid={`team-order-amount-save-${order.id}`}>OK</Button>
            )}
          </div>
        </div>
      </div>

      {/* Fotos */}
      {(order.photos || []).length > 0 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid={`team-order-photos-${order.id}`}>
          {order.photos.map((p) => (
            <a key={p.id} href={`${API_BASE}/api/files/download/${encodeURI(p.storage_path)}?auth=${encodeURIComponent(token || "")}`} target="_blank" rel="noreferrer" className="shrink-0">
              <img
                src={`${API_BASE}/api/files/download/${encodeURI(p.storage_path)}?auth=${encodeURIComponent(token || "")}`}
                alt={p.original_filename}
                className="h-20 w-20 object-cover rounded-lg border border-zinc-200 hover:opacity-80 transition-opacity"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}

      {/* Aktionen */}
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        {!order.case_id ? (
          <Button onClick={takeOver} disabled={busy} className="brand-gradient-bg text-white" data-testid={`team-order-takeover-${order.id}`}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
            Übernehmen → Akte anlegen
          </Button>
        ) : (
          <Link to={`/cases/${order.case_id}`}>
            <Button variant="outline" data-testid={`team-order-open-case-${order.id}`}>
              <FileText className="h-4 w-4 mr-2" /> Akte öffnen
            </Button>
          </Link>
        )}
        {order.status !== "paid" && (
          <Button
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={markPaid}
            disabled={busy}
            title="Erst nach Zahlungseingang der Versicherung markieren"
            data-testid={`team-order-markpaid-${order.id}`}
          >
            <Banknote className="h-4 w-4 mr-2" /> Als ausgezahlt markieren
          </Button>
        )}
        {order.status === "paid" && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> Ausgezahlt am {fmtDate(order.commission_paid_at)}
          </span>
        )}
      </div>
    </div>
  );
}

const FILTERS = [
  { key: "", label: "Alle" },
  { key: "submitted", label: "Eingereicht" },
  { key: "in_progress", label: "In Bearbeitung" },
  { key: "report_done", label: "Gutachten fertig" },
  { key: "paid", label: "Ausgezahlt" },
];

export default function PartnerOrders() {
  const { accessToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner-orders", { params: filter ? { status: filter } : {} });
      setOrders(data.orders || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const newCount = orders.filter((o) => o.status === "submitted").length;

  return (
    <Layout title="Partner-Aufträge" subtitle="Eingereichte Aufträge der Provisionspartner — übernehmen, bearbeiten, auszahlen.">
      <div className="space-y-4" data-testid="partner-orders-page">
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                filter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid={`orders-filter-${f.key || "all"}`}
            >
              {f.label}
            </button>
          ))}
          {newCount > 0 && filter === "" && (
            <Badge className="bg-red-500 text-white border-0" data-testid="orders-new-count">{newCount} neu</Badge>
          )}
          <Button variant="ghost" size="sm" onClick={load} className="ml-auto" data-testid="orders-refresh-btn">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Aktualisieren
          </Button>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-300" /></div>
        ) : orders.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-400" data-testid="team-orders-empty">
            <Camera className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
            Keine Partner-Aufträge {filter ? "mit diesem Status" : "vorhanden"}.
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} token={accessToken} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
